/**
 * Router - 路由 + 鉴权守卫.
 *
 * 设计 (与 admin-web 对齐):
 *   - 守卫 #1: 鉴权, 看 getAccessToken(); 无 token 且路由需 auth -> 跳 /login (带 redirect).
 *   - 守卫 #2: bootstrap 当前用户, 首次进入 authed 路由时 fetchMe() (非阻塞, 失败回登录).
 *   - 用 createWebHashHistory: Capacitor 打包后用 file:// 加载, hash 模式更稳.
 *
 * 路由结构 (ChatGPT 风格侧栏布局, 底部 tab 已移除):
 *   /login                     登录页 (不进 AppLayout)
 *   /  -> /chat                军师欢迎页 (主区空态, 侧栏选角)
 *      /chat/conversation/:id  对话详情 (主区, 头部汉堡开侧栏)
 *   /studio                    数字员工 (主区)
 *   /me                        我的 (主区)
 *   注: meta.tab 保留但 AppLayout 不再消费 (旧版底部 tab 遗留, 可选清理).
 */
import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router'
import AppLayout from '@/layouts/AppLayout.vue'
import { getAccessToken, redirectToLogin } from '@/api/client'
import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Login.vue'),
    meta: { requiresAuth: false, title: '登录' },
  },
  {
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: { name: 'chat' },
      },
      {
        path: 'chat',
        name: 'chat',
        component: () => import('@/views/chat/ChatHome.vue'),
        meta: { requiresAuth: true, title: '军师', tab: 'chat' },
      },
      {
        path: 'chat/role/:id',
        name: 'role-intro',
        component: () => import('@/views/chat/RoleIntro.vue'),
        meta: { requiresAuth: true, title: '军师介绍', tab: 'chat' },
      },
      {
        path: 'chat/conversation/:id?',
        name: 'conversation',
        component: () => import('@/views/chat/Conversation.vue'),
        meta: { requiresAuth: true, title: '对话', tab: 'chat' },
      },
      {
        path: 'studio',
        name: 'studio',
        component: () => import('@/views/studio/StudioHome.vue'),
        meta: { requiresAuth: true, title: '数字员工', tab: 'studio' },
      },
      {
        path: 'me',
        name: 'me',
        component: () => import('@/views/me/Profile.vue'),
        meta: { requiresAuth: true, title: '我的', tab: 'me' },
      },
    ],
  },
  {
    // 兜底 -> 军师
    path: '/:pathMatch(.*)*',
    redirect: { name: 'chat' },
  },
]

const router = createRouter({
  history: createWebHashHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
})

// 守卫 #1: 鉴权
router.beforeEach((to) => {
  const hasToken = !!getAccessToken()
  const requiresAuth = to.meta.requiresAuth !== false
  if (requiresAuth && !hasToken) {
    return {
      path: '/login',
      query: to.fullPath && to.fullPath !== '/' ? { redirect: to.fullPath } : undefined,
    }
  }
  if (to.path === '/login' && hasToken) {
    return { path: '/chat' }
  }
  if (to.meta.title) {
    document.title = `${to.meta.title} · 知行AI`
  }
  return true
})

// 守卫 #2: 首次进入 authed 区, 拉一次当前用户 (非阻塞).
let userBootstrapped = false
router.beforeEach(async () => {
  if (userBootstrapped) return
  if (!getAccessToken()) {
    userBootstrapped = true
    return
  }
  userBootstrapped = true
  const auth = useAuthStore()
  auth.fetchMe().catch(() => {
    redirectToLogin()
  })
  return true
})

export default router
